import { createFileRoute } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { requireFeature } from '@/common/utils/requireFeature';
import { techSystemsService } from '@/features/tech-systems/services/tech-systems.service';
import { PermissionGate } from '@/components/PermissionGate';
import { Permission } from '@/common/enums/permissions.enum';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useState } from 'react';
import { useToast } from '@/hooks/use-toast';
import { FinanceUsersPicker } from '@/features/tech-systems/components/FinanceUsersPicker';
import { BusinessHolidaysSection } from '@/features/tech-systems/project-templates/components/BusinessHolidaysSection';

export const Route = createFileRoute('/_authenticated/tech-systems/config')({
  beforeLoad: () => requireFeature('TECH_SYSTEMS'),
  component: TechSystemsConfigPage,
});

function TechSystemsConfigPage() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { data: settings, isLoading } = useQuery({
    queryKey: ['tech-systems-config'],
    queryFn: () => techSystemsService.getConfig(),
  });

  const [pingInterval, setPingInterval] = useState('');
  const [failureThreshold, setFailureThreshold] = useState('');
  const [backupCheckHour, setBackupCheckHour] = useState('');
  const [tokenLabel, setTokenLabel] = useState('');
  const [tokenValue, setTokenValue] = useState('');

  const { data: githubTokens = [] } = useQuery({
    queryKey: ['tech-systems-github-tokens'],
    queryFn: () => techSystemsService.listGithubTokens(),
  });

  const saveSettings = useMutation({
    mutationFn: () =>
      techSystemsService.updateConfig({
        pingIntervalSeconds:
          pingInterval.trim() === ''
            ? settings?.pingIntervalSeconds
            : parseInt(pingInterval, 10),
        defaultFailureThreshold:
          failureThreshold.trim() === ''
            ? settings?.defaultFailureThreshold
            : parseInt(failureThreshold, 10),
        backupCheckHour:
          backupCheckHour.trim() === ''
            ? settings?.backupCheckHour
            : parseInt(backupCheckHour, 10),
      }),
    onSuccess: () => {
      toast({ title: 'Configurações salvas' });
      queryClient.invalidateQueries({ queryKey: ['tech-systems-config'] });
    },
  });

  const addToken = useMutation({
    mutationFn: () =>
      techSystemsService.createGithubToken({
        label: tokenLabel,
        token: tokenValue,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tech-systems-github-tokens'] });
      setTokenLabel('');
      setTokenValue('');
      toast({ title: 'Token cadastrado' });
    },
  });

  if (isLoading) return <div className="p-6">Carregando...</div>;

  return (
    <PermissionGate permission={Permission.TECH_SYSTEMS_CONFIG}>
      <div className="space-y-6 p-6 max-w-2xl">
        <h1 className="text-2xl font-semibold">Configuração — Sistemas TEC</h1>

        <Card>
          <CardHeader>
            <CardTitle>Controle de ping</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div>
              <Label>Intervalo entre ciclos (segundos)</Label>
              <Input
                type="number"
                defaultValue={settings?.pingIntervalSeconds}
                onChange={(e) => setPingInterval(e.target.value)}
              />
            </div>
            <div>
              <Label>Falhas para considerar down</Label>
              <Input
                type="number"
                defaultValue={settings?.defaultFailureThreshold}
                onChange={(e) => setFailureThreshold(e.target.value)}
              />
            </div>
            <div>
              <Label>Hora diária para verificar alertas de backup (0-23)</Label>
              <Input
                type="number"
                min={0}
                max={23}
                defaultValue={settings?.backupCheckHour ?? 9}
                onChange={(e) => setBackupCheckHour(e.target.value)}
              />
            </div>
            <Button onClick={() => saveSettings.mutate()}>Salvar</Button>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Responsáveis financeiros</CardTitle>
          </CardHeader>
          <CardContent>
            <FinanceUsersPicker />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Tokens GitHub</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <ul className="text-sm">
              {(githubTokens as any[]).map((t) => (
                <li key={t.id}>{t.label}</li>
              ))}
            </ul>
            <Input
              placeholder="Label"
              value={tokenLabel}
              onChange={(e) => setTokenLabel(e.target.value)}
            />
            <Input
              placeholder="PAT token"
              type="password"
              value={tokenValue}
              onChange={(e) => setTokenValue(e.target.value)}
            />
            <Button onClick={() => addToken.mutate()}>Cadastrar token</Button>
          </CardContent>
        </Card>

        <BusinessHolidaysSection />
      </div>
    </PermissionGate>
  );
}
